import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  Target,
  Users,
  Handshake,
  Send,
  MessageSquare,
  TrendingUp,
  Zap,
  Plus,
  AlertCircle,
} from 'lucide-react'

import { cn } from 'components/UI/cn'
import type { Campaign } from 'models/campaign'
import { WORKFLOW_STATUS } from 'models/campaign'

interface Props {
  campaign: Campaign
  campaignId: string
  hasSequence: boolean
}

export function CampaignDetailOverviewTab({
  campaign,
  campaignId,
  hasSequence,
}: Props) {
  const navigate = useNavigate()

  const hasTargets = campaign.contact_company_count > 0
  const hasContacts = campaign.contact_count > 0
  const hasLaunchDate = !!campaign.launch_date
  const hasContactsRequested =
    campaign.workflow_status !== WORKFLOW_STATUS.DRAFT
  const checklistItems = [
    {
      label: 'Campaign created',
      done: true,
      detail: undefined as string | undefined,
    },
    {
      label: 'Targets assigned',
      done: hasTargets,
      detail: hasTargets
        ? `${campaign.contact_company_count} targets`
        : undefined,
    },
    {
      label: 'Contacts requested',
      done: hasContactsRequested,
      detail: hasContacts ? `${campaign.contact_count} contacts` : undefined,
    },
    {
      label: 'Email sequence built',
      done: hasSequence,
      detail: hasSequence ? 'Steps configured' : undefined,
    },
    {
      label: 'Projected launch date set',
      done: hasLaunchDate,
      detail: hasLaunchDate
        ? new Date(campaign.launch_date!).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : undefined,
    },
  ]

  const completedCount = checklistItems.filter((item) => item.done).length
  const progressPct = Math.round((completedCount / checklistItems.length) * 100)

  const statCards = [
    {
      label: 'Targets',
      value: hasTargets ? campaign.contact_company_count : '—',
      icon: Target,
    },
    {
      label: 'Contacts',
      value: hasContacts ? campaign.contact_count : '—',
      icon: Users,
    },
    { label: 'Deals', value: '—', icon: Handshake },
    { label: 'Emails Sent', value: '—', icon: Send },
    { label: 'Reply Rate', value: '—', icon: MessageSquare },
    { label: 'Interested Rate', value: '—', icon: TrendingUp },
  ]

  const getNextStepsMessage = () => {
    if (!hasSequence) {
      return 'Create your email sequence to start reaching your targets.'
    }
    if (!hasContactsRequested) {
      return 'Request contacts to start your outreach.'
    }
    if (!hasLaunchDate) {
      return 'Set a projected launch date to finalize your campaign.'
    }
    return 'Review your setup and launch when ready.'
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="bg-surface rounded-lg border border-line p-3"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-fg-secondary capitalize">
                {stat.label}
              </span>
              <stat.icon className="size-4 text-brand-hover" />
            </div>
            <p
              className={cn(
                'text-base font-bold',
                stat.value === '—' ? 'text-fg-muted' : 'text-fg'
              )}
            >
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-surface rounded-lg border border-line p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base text-fg">Setup Progress</h3>
            <span className="text-xs text-fg-secondary bg-surface-muted px-2 py-0.5 rounded-full">
              {completedCount}/{checklistItems.length}
            </span>
          </div>
          <div className="w-full h-2 bg-surface-alt rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-brand rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="space-y-2.5">
            {checklistItems.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                {item.done ? (
                  <CheckCircle2 className="size-4 text-brand shrink-0" />
                ) : (
                  <div className="size-4 rounded-full border-2 border-line shrink-0" />
                )}
                <span
                  className={cn(
                    'text-sm',
                    item.done ? 'text-fg' : 'text-fg-muted'
                  )}
                >
                  {item.label}
                </span>
                {item.detail && (
                  <span className="text-xxs text-fg-secondary bg-surface-alt px-2 py-0.5 rounded-full ml-auto">
                    {item.detail}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface rounded-lg border border-line p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base text-fg">Email Sequence</h3>
            {hasSequence && (
              <button
                onClick={() => navigate(`/campaigns/${campaignId}/sequence`)}
                className="text-sm text-brand-hover hover:underline"
              >
                Edit Sequence
              </button>
            )}
          </div>

          {hasSequence ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-sm text-fg-secondary">Sequence configured.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="size-12 rounded-2xl bg-surface-hover flex items-center justify-center mb-3">
                <Zap className="size-6 text-brand" />
              </div>
              <p className="text-sm text-fg mb-1">No sequence yet</p>
              <p className="text-xs text-fg-secondary mb-4 max-w-[260px]">
                Build a multi-step email sequence to reach your targets.
              </p>
              <button
                onClick={() => navigate(`/campaigns/${campaignId}/sequence`)}
                className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
              >
                <Plus className="size-3.5" />
                Create Sequence
              </button>
            </div>
          )}
        </div>
      </div>

      {completedCount < checklistItems.length && (
        <div className="bg-info-bg rounded-lg p-3.5 flex items-start gap-2.5">
          <AlertCircle className="size-4 text-info shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-fg">Next steps</p>
            <p className="text-xs text-fg-secondary mt-0.5">
              {getNextStepsMessage()}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
